/**
 * SessionTimeline — a live, lane-by-lane scoreboard for the running session.
 *
 * The server-authored session script is a real time-based audio board:
 *   - music            : continuous beds (start/end seconds)
 *   - main_voice       : foreground hypnosis narration at at_seconds
 *   - whispers         : left/right whisper cues at at_seconds
 *   - spatial_encounters: positional encounter cues at at_seconds
 *   - subliminal.events: processed subliminal one-shots / continuous bed
 *   - loop_tail        : the 10-minute always-deeper tail that repeats forever
 *
 * This component renders that score as a horizontal timeline so the Director
 * can SEE exactly what lane plays what, and when — the main voice, the music,
 * the whispers, the subliminal, all on one clock. A playhead sweeps across it
 * in real time.
 *
 * Lane colors follow the canonical lane contract (audio-lanes.js):
 *   music_drops        -> deep violet
 *   hypnosis_spoken_main -> warm amber (the foreground voice)
 *   whisper_left/right  -> cool cyan / teal
 *   subliminal_1/2      -> magenta (processed, low-salience)
 *   spatial_encounter_* -> soft green
 */
import { useMemo, useState, useEffect } from "react";
import {
    LANE_MUSIC_DROPS,
    LANE_HYPNOSIS_SPOKEN_MAIN,
    LANE_WHISPER_LEFT,
    LANE_WHISPER_RIGHT,
    LANE_SUBLIMINAL_1,
    LANE_SUBLIMINAL_2,
    LANE_OVERLAY,
    isWhisperLane,
    isEncounterLane,
    isSubliminalLane,
} from "./audio-lanes.js";

const LANE_COLORS = {
    [LANE_MUSIC_DROPS]: "#8b5cf6",
    [LANE_HYPNOSIS_SPOKEN_MAIN]: "#f59e0b",
    [LANE_WHISPER_LEFT]: "#22d3ee",
    [LANE_WHISPER_RIGHT]: "#2dd4bf",
    [LANE_SUBLIMINAL_1]: "#e879f9",
    [LANE_SUBLIMINAL_2]: "#c084fc",
    [LANE_OVERLAY]: "#a3a3a3",
    encounter: "#4ade80",
};

const LANE_LABELS = {
    [LANE_MUSIC_DROPS]: "Music / Drops",
    [LANE_HYPNOSIS_SPOKEN_MAIN]: "Main Voice",
    [LANE_WHISPER_LEFT]: "Whisper L",
    [LANE_WHISPER_RIGHT]: "Whisper R",
    [LANE_SUBLIMINAL_1]: "Subliminal 1",
    [LANE_SUBLIMINAL_2]: "Subliminal 2",
    [LANE_OVERLAY]: "Overlay",
    encounter: "Encounters",
};

function laneColor(lane) {
    if (isEncounterLane(lane)) return LANE_COLORS.encounter;
    return LANE_COLORS[lane] || LANE_COLORS[LANE_OVERLAY];
}

function laneLabel(lane) {
    if (isEncounterLane(lane)) return LANE_LABELS.encounter;
    return LANE_LABELS[lane] || lane;
}

function fmtClock(seconds) {
    const s = Math.max(0, Math.floor(Number(seconds) || 0));
    const m = Math.floor(s / 60);
    const r = s % 60;
    return `${String(m).padStart(2, "0")}:${String(r).padStart(2, "0")}`;
}

function shortLabel(text, max = 26) {
    const clean = String(text || "").replace(/\s+/g, " ").trim();
    if (clean.length <= max) return clean;
    return `${clean.slice(0, max - 1)}…`;
}

/**
 * Build the ordered lane rows and their events from a session script score.
 * Returns [{ lane, label, color, events: [{start, end, label, kind}] }]
 */
function buildTimeline(script) {
    // Must always return the same shape. Returning a bare [] here used to make
    // the caller's `const { rows, target } = ...` yield rows === undefined,
    // which threw on `rows.length` and unmounted the whole React root — the
    // "solid black screen + audio stops" failure.
    if (!script || typeof script !== "object") return { rows: [], target: 3600 };
    const target = Math.max(60, Number(script.target_seconds) || 3600);
    const rows = new Map();

    const ensureRow = (lane) => {
        if (!rows.has(lane)) {
            rows.set(lane, { lane, label: laneLabel(lane), color: laneColor(lane), events: [] });
        }
        return rows.get(lane);
    };

    const push = (lane, start, end, label, kind = "event") => {
        const row = ensureRow(lane);
        row.events.push({ start, end, label, kind });
    };

    // Music beds — continuous, so render as full-width bands.
    const music = Array.isArray(script.music) ? script.music : [];
    if (music.length) {
        const row = ensureRow(LANE_MUSIC_DROPS);
        music.forEach((m) => {
            const start = Math.max(0, Number(m.start_seconds) || 0);
            const end = Math.min(target, Number(m.end_seconds) || start + 60);
            row.events.push({
                start,
                end,
                label: shortLabel(m.file || m.category || "music"),
                kind: "music",
            });
        });
    } else {
        // No explicit music score — show the whole session as one continuous bed.
        ensureRow(LANE_MUSIC_DROPS).events.push({
            start: 0, end: target, label: "continuous bed", kind: "music",
        });
    }

    // Foreground main voice.
    (Array.isArray(script.main_voice) ? script.main_voice : []).forEach((e) => {
        const at = Math.max(0, Number(e.at_seconds) || 0);
        const dur = Math.max(4, Number(e.duration_seconds) || 8);
        push(LANE_HYPNOSIS_SPOKEN_MAIN, at, Math.min(target, at + dur),
            shortLabel(e.summary || e.file || e.role || "voice"), "voice");
    });

    // Bundle sessions carry authored programs (slug/title/category/script) rather
    // than a timed main_voice score. Render each program as a main-voice block
    // spread evenly across the session so the scoreboard still shows the arc.
    const programs = Array.isArray(script.programs) ? script.programs : [];
    if (programs.length && !(Array.isArray(script.main_voice) && script.main_voice.length)) {
        const step = target / programs.length;
        programs.forEach((p, i) => {
            push(LANE_HYPNOSIS_SPOKEN_MAIN, i * step, Math.min(target, (i + 1) * step),
                shortLabel(p.title || p.category || p.slug || "program"), "voice");
        });
    }

    // Whispers — split left/right by lane.
    (Array.isArray(script.whispers) ? script.whispers : []).forEach((e) => {
        const at = Math.max(0, Number(e.at_seconds) || 0);
        const dur = Math.max(3, Number(e.duration_seconds) || 6);
        const lane = isWhisperLane(String(e.lane || ""))
            ? String(e.lane)
            : String(e.target_speaker || "") === "right"
                ? LANE_WHISPER_RIGHT
                : LANE_WHISPER_LEFT;
        push(lane, at, Math.min(target, at + dur),
            shortLabel(e.summary || e.file || "whisper"), "whisper");
    });

    // Spatial encounters.
    (Array.isArray(script.spatial_encounters) ? script.spatial_encounters : []).forEach((e) => {
        const at = Math.max(0, Number(e.at_seconds) || 0);
        const dur = Math.max(4, Number(e.duration_seconds) || 8);
        push(String(e.lane || "encounter_"), at, Math.min(target, at + dur),
            shortLabel(e.summary || e.file || "encounter"), "encounter");
    });

    // Subliminal events.
    const sub = script.subliminal && Array.isArray(script.subliminal.events)
        ? script.subliminal.events
        : [];
    sub.forEach((e) => {
        const at = Math.max(0, Number(e.at_seconds) || 0);
        const dur = e.continuous_loop ? target - at : Math.max(6, Number(e.duration_seconds) || 10);
        push(LANE_SUBLIMINAL_1, at, Math.min(target, at + dur),
            shortLabel(e.file || e.role || "subliminal"), "subliminal");
    });

    // Loop tail marker.
    const tailStart = Math.max(0, Number(script.loop_tail?.starts_at_seconds) || target);
    if (tailStart < target) {
        ensureRow(LANE_MUSIC_DROPS).events.push({
            start: tailStart, end: target, label: "always-deeper tail (loops)", kind: "tail",
        });
    }

    // Sort rows in canonical lane order, then events by start.
    const order = [
        LANE_MUSIC_DROPS,
        LANE_HYPNOSIS_SPOKEN_MAIN,
        LANE_OVERLAY,
        LANE_WHISPER_LEFT,
        LANE_WHISPER_RIGHT,
        LANE_SUBLIMINAL_1,
        LANE_SUBLIMINAL_2,
    ];
    const sorted = Array.from(rows.values()).sort((a, b) => {
        const ia = order.indexOf(a.lane);
        const ib = order.indexOf(b.lane);
        if (ia !== -1 && ib !== -1) return ia - ib;
        if (ia !== -1) return -1;
        if (ib !== -1) return 1;
        return a.lane.localeCompare(b.lane);
    });
    sorted.forEach((row) => row.events.sort((a, b) => a.start - b.start));
    return { rows: sorted, target };
}

/**
 * The live scoreboard. `script` is the session script; `elapsedSeconds` is the
 * current session clock (0 when not playing). `open` toggles visibility.
 */
export function SessionTimeline({ script, elapsedSeconds = 0, open = false, onClose }) {
    const [now, setNow] = useState(() => Number(elapsedSeconds) || 0);

    // Keep the playhead live even when the parent only updates on state change.
    useEffect(() => {
        if (!open) return undefined;
        const id = window.setInterval(() => {
            setNow((prev) => prev + 0.25);
        }, 250);
        return () => window.clearInterval(id);
    }, [open]);

    // When the parent reports a fresh authoritative clock, adopt it.
    useEffect(() => {
        if (Number.isFinite(Number(elapsedSeconds))) setNow(Number(elapsedSeconds));
    }, [elapsedSeconds]);

    const { rows, target } = useMemo(() => {
        const built = buildTimeline(script);
        return {
            rows: Array.isArray(built?.rows) ? built.rows : [],
            target: Number(built?.target) || 3600,
        };
    }, [script]);
    if (!open) return null;

    const playheadPct = target > 0 ? Math.min(100, (now / target) * 100) : 0;

    return (
        <div className="session-timeline" role="region" aria-label="Session timeline scoreboard">
            <div className="session-timeline-header">
                <span className="session-timeline-title">Session Scoreboard</span>
                <span className="session-timeline-clock">{fmtClock(now)} / {fmtClock(target)}</span>
                <button type="button" className="session-timeline-close" aria-label="Close scoreboard" onClick={onClose}>×</button>
            </div>
            <div className="session-timeline-body">
                {rows.length === 0 ? (
                    <p className="session-timeline-empty">No session score yet — build a session to see its timeline.</p>
                ) : (
                    rows.map((row) => (
                        <div className="session-timeline-row" key={row.lane}>
                            <div className="session-timeline-lane">
                                <span className="session-timeline-dot" style={{ background: row.color }} />
                                <span className="session-timeline-lane-label">{row.label}</span>
                            </div>
                            <div className="session-timeline-track">
                                {row.events.map((ev, i) => {
                                    const left = target > 0 ? (ev.start / target) * 100 : 0;
                                    const width = target > 0 ? Math.max(0.6, ((ev.end - ev.start) / target) * 100) : 0;
                                    return (
                                        <div
                                            key={`${ev.start}-${i}`}
                                            className={`session-timeline-event session-timeline-event--${ev.kind}`}
                                            style={{ left: `${left}%`, width: `${width}%`, background: row.color }}
                                            title={`${fmtClock(ev.start)} → ${fmtClock(ev.end)} · ${ev.label}`}
                                        >
                                            <span className="session-timeline-event-label">{ev.label}</span>
                                        </div>
                                    );
                                })}
                                <div className="session-timeline-playhead" style={{ left: `${playheadPct}%` }} />
                            </div>
                        </div>
                    ))
                )}
            </div>
            <div className="session-timeline-footer">
                <span>Music bed runs continuously beneath every lane.</span>
                <span className="session-timeline-legend">
                    <i style={{ background: LANE_COLORS[LANE_HYPNOSIS_SPOKEN_MAIN] }} /> main voice
                    <i style={{ background: LANE_COLORS[LANE_WHISPER_LEFT] }} /> whispers
                    <i style={{ background: LANE_COLORS[LANE_SUBLIMINAL_1] }} /> subliminal
                    <i style={{ background: LANE_COLORS.encounter }} /> encounters
                </span>
            </div>
        </div>
    );
}